import {getAnalytics} from '@/hooks/useAnalytics';
import type {
  ErrorWidget,
  GetNextBuyflowStepResponse,
  SchedulingWidget,
  TimeSlot,
} from '@/lib/TypesOpenApi';
import type {DeepReadonly} from '@/lib/typings/readonly';
import {persist} from '@/lib/vatio-persistence';
import {memoize} from 'proxy-memoize';
import {
  type INTERNAL_Snapshot,
  proxy,
  snapshot,
  subscribe,
  useSnapshot,
} from 'valtio';
import {watch} from 'valtio/utils';
import {
  type StepWidgetStore,
  StepsPath,
  stepsMap,
} from '../routing/StepsRouting';
import {useWidget} from '../routing/StepsWizardContext';

export interface SchedulingDate {
  weekDay: string;
  monthDay: string;
  month: string;
  appointments: Array<Appointment>;
}

export interface Appointment {
  id: string;
  text: string;
  timeZone: string;
}

export const widget = stepsMap['SCHEDULING']!.store;

export interface SchedulingPageWidget {
  /** Base widget to relay on */
  widget: StepWidgetStore;
  content?: GetNextBuyflowStepResponse;
  /** synchronized version of widget. For internal usage within computed props */
  productWidget?: SchedulingWidget;
  /** Available slots to select */
  days: SchedulingDate[];
  findById(id: string): Appointment | undefined;
  /** Definition for possible client side error */
  errorUnavailableAppt?: ErrorWidget;
}

const memoizedDates = memoize((snap: INTERNAL_Snapshot<SchedulingPageWidget>) =>
  toSchedulingDates(snap.productWidget?.timeSlots || []),
);

export const schedulingWidget = proxy<SchedulingPageWidget>({
  widget,
  get content() {
    return this.widget.content;
  },

  get productWidget(): SchedulingWidget | undefined {
    // extract product info from widget
    return this.content?.buyflowStep.buyflowWidgets?.map(
      ({schedulingWidget}) => schedulingWidget,
    )[0];
  },

  get days(): Array<SchedulingDate> {
    return memoizedDates(snapshot(schedulingWidget));
  },

  findById(id: string): Appointment | undefined {
    for (const day of schedulingWidget.days) {
      const found = day.appointments.find(
        ({id: appointmentId}) => appointmentId === id,
      );

      if (found) {
        return found;
      }
    }
  },

  get errorUnavailableAppt(): ErrorWidget | undefined {
    return this.content?.buyflowStep.buyflowWidgets
      .flatMap(bfw => {
        return bfw.errorWidgets;
      })
      .find(ew => ew?.errorType === 'SELECTED_APPOINTMENT_UNAVAILABLE');
  },
});

export const useSchedulingWidget = () =>
  useWidget(StepsPath.Schedule, schedulingWidget);
export interface DateTimeSlot {
  /** Stringfied date for fast comaprision and search */
  id: string;
}

/**
 * Scheduled appointment info
 * Only appointment field can be set, rest is calculated automatically
 * */
export interface ScheduleState {
  appointment?: Appointment;
  /** selected slot number in days slots array */
  dayIndex?: number;
  day?: SchedulingDate;

  timeSlot?: TimeSlot;
  isErrorDialogOpen: boolean;
}

// if add any new field, add the new field to the below reset function also
export const store = persist<ScheduleState>(StepsPath.Schedule, {
  appointment: undefined,
  // Save timeslot together with appointment in local storage, as it required for API payload
  timeSlot: undefined,

  // require sync between scheduling widget and state, will be synchronized in watch
  dayIndex: undefined,
  day: undefined,
  isErrorDialogOpen: false,
});

export const resetScheduleState = () => {
  store.appointment = undefined;
  store.timeSlot = undefined;
  store.dayIndex = undefined;
  store.isErrorDialogOpen = false;
};

export const useSchedulingState = () => useSnapshot(store);

// by default first appointment preselected
function preselectFirstAppointment() {
  // function must be called first time on page load,
  // and later on updates, in case if widget was loaded not from start
  const firstSlot = schedulingWidget.days[0]?.appointments?.[0];
  if (!firstSlot) {
    // if not have first slot, then widget not loaded
    return;
  }

  if (store.appointment) {
    // already exists appointment, probably we reload page, will check if valid
    if (schedulingWidget.findById(store.appointment.id)) {
      // selected appointment exists and valid, will keep it
      const analytics = getAnalytics();
      analytics.trackScheduleTimeSelected(
        getDurationInDays(new Date(store.appointment.id)),
      );
      return;
    }
    setErrorDialogOpen(true);
  }

  // appointment missing or invalid, will select first slot
  chooseAppointment(firstSlot.id); // do not set directly, to save timeSlot also
}

preselectFirstAppointment();

subscribe(schedulingWidget, () => {
  const analytics = getAnalytics();

  if (schedulingWidget?.days?.length) {
    analytics.setAvailableAppointmentsTimestamps(
      schedulingWidget.days
        .reduce((appoints: Appointment[], day: SchedulingDate) => {
          return appoints.concat(day?.appointments || []);
        }, [])
        .map((app: Appointment) => {
          const firstAppointment = app.id;
          const date = firstAppointment && new Date(firstAppointment);
          const dateSecs = date ? Math.floor(date.getTime() / 1000) : 0;
          return dateSecs;
        }),
    );
  }

  preselectFirstAppointment();

  if (store.appointment) {
    const firstAppointment = schedulingWidget.days?.[0]?.appointments?.[0]?.id;
    analytics.setFirstInstallDateNonIru(firstAppointment);
  }

  analytics.trackScheduleLoaded();
});

watch(get => {
  const {appointment, dayIndex} = get(store);
  const {days} = get(schedulingWidget);

  if (!appointment) {
    if (store.dayIndex)
      // will clear dayIndex only if it was set, to avoid additional rerenders
      store.dayIndex = undefined;
    store.day = undefined;
    return;
  }

  const index = days.findIndex(
    ({appointments}) =>
      appointments.findIndex(({id}) => id === appointment.id) !== -1,
  );

  const newDayIndex = index !== -1 ? index : undefined;
  if (newDayIndex !== dayIndex) {
    // set only if differ to avoid rerenders
    store.dayIndex = newDayIndex;
  }

  if (typeof newDayIndex !== 'undefined') {
    // if new dayIndex unknown, probably scheduling widget content empty, like after page refresh
    // will keep old day content, to keep appointment wisible on other pages
    store.day = schedulingWidget.days[newDayIndex];
  }
});

export function getDurationInDays(futureDate: Date = new Date()): number {
  const msInDay = 1000 * 60 * 60 * 24;
  const diffInMs = Math.abs(+futureDate - +new Date());
  return Math.floor(diffInMs / msInDay);
}

export function setErrorDialogOpen(isOpen: boolean) {
  store.isErrorDialogOpen = isOpen;
}

/**
 * Set timeslot based on start date of timeslot
 * @param date: includes correct day and time of slot
 * */
export function chooseAppointment(id?: string) {
  if (!id) {
    // undefiend id means we want to clear it
    store.appointment = undefined;
    store.timeSlot = undefined;
    return;
  }

  const found = schedulingWidget.findById(id);
  if (!found) {
    console.warn('Cannot find an appointment');
    return;
  }

  if (store.appointment?.id !== found?.id) {
    const analytics = getAnalytics();
    analytics.trackScheduleTimeSelected(getDurationInDays(new Date(found.id)));
  }

  store.appointment = found;
  store.timeSlot = schedulingWidget.productWidget?.timeSlots?.find(
    slot => slot.startDateTime === id,
  );
}

function toSchedulingDates(
  slots: DeepReadonly<TimeSlot[]>,
): Array<SchedulingDate> {
  const daysMap = slots.reduce(
    (result: {[id: string]: SchedulingDate}, startTime: any) => {
      const slot = new Date(startTime.startDateTime!);
      const lang = navigator.language;
      const day: SchedulingDate = {
        weekDay: slot.toLocaleDateString(lang, {
          weekday: 'long',
          timeZone: startTime.timezone,
        }),
        month: slot.toLocaleDateString(lang, {
          month: 'long',
          timeZone: startTime.timezone,
        }),
        monthDay: slot.toLocaleDateString(lang, {
          day: 'numeric',
          timeZone: startTime.timezone,
        }),
        appointments: [
          {
            id: startTime.startDateTime!,
            timeZone: startTime.timezone,
            text: slot.toLocaleTimeString(lang, {
              hour: 'numeric',
              minute: 'numeric',
              timeZone: startTime.timezone,
            }),
          },
        ],
      };

      const id = `${day.month}/${day.monthDay}`;

      if (result[id]) {
        // day already exists, will add new entry in appointments
        result[id].appointments.push(day.appointments[0]);
        return result;
      }

      result[id] = day;

      return result;
    },
    {} as {[id: string]: SchedulingDate},
  );

  return Object.values(daysMap); // TODO: add days and appointments sorting
}

/** Map date to API timeslot format, date-time, e.g. 01/02/2023-01:01:01 */
export function toTimeslotFormat(date: Date) {
  return date.toISOString();
}
